/**
 * CommunitiesSection — joined communities as compact editorial rows, not
 * the large discovery cards used on the Communities landing page. Profile
 * is a preview, not a re-implementation of that page.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Users } from 'lucide-react';
import { SectionHeader, cx } from '@/components/site/primitives';
import { fadeUp, stagger, viewport } from '@/lib/motion';
import { formatCount } from './utils/profileHelpers';
import EmptyState from './EmptyState';
import { PROFILE } from '@/constants/testIds';

const SPINE_TONE = {
  coral: 'from-coral to-[#b85e58]',
  yellow: 'from-yellow to-[#e0b164]',
  sage: 'from-sage to-[#a9c39d]',
  blue: 'from-blue to-[#6b808b]',
};

function CommunityRow({ community }) {
  return (
    <Link
      to={`/app/communities/${community.id}`}
      className="group flex items-center gap-3.5 rounded-xl px-2 py-3 transition-colors hover:bg-card"
    >
      <span className={cx('grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br font-display text-base font-bold text-[#FFF7F0]', SPINE_TONE[community.tone] || SPINE_TONE.coral)}>
        {community.name[0]}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-display text-[15px] font-semibold text-ink group-hover:text-coral">{community.name}</p>
        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-subtle">
          <Users className="h-3 w-3" /> {formatCount(community.memberCount)} members
          {community.role && community.role !== 'member' && <span className="ml-1 rounded-full bg-coral/12 px-2 py-0.5 text-[10px] font-semibold text-coral">{community.role}</span>}
        </p>
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 text-subtle opacity-0 transition-opacity group-hover:opacity-100" />
    </Link>
  );
}

export default function CommunitiesSection({ communities, limit }) {
  const visible = limit ? communities.slice(0, limit) : communities;

  return (
    <section className="u-container mt-16 pb-4 md:mt-24" data-testid={PROFILE.communities}>
      <div className="flex items-end justify-between gap-4">
        <SectionHeader eyebrow="Reading together" title="My Communities" />
        <Link to="/app/communities" className="group mb-1 hidden shrink-0 items-center gap-1.5 text-sm font-semibold text-coral sm:flex">
          Explore Communities <ArrowRight className="h-4 w-4 transition-transform duration-300 ease-soft group-hover:translate-x-1" />
        </Link>
      </div>

      {visible.length === 0 ? (
        <EmptyState type="communities" to="/app/communities" compact className="mt-2" />
      ) : (
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="mt-6 grid grid-cols-1 gap-x-6 sm:grid-cols-2"
        >
          {visible.map((community) => (
            <motion.div key={community.id} variants={fadeUp}>
              <CommunityRow community={community} />
            </motion.div>
          ))}
        </motion.div>
      )}

      <Link to="/app/communities" className="group mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-coral sm:hidden">
        Explore Communities <ArrowRight className="h-4 w-4 transition-transform duration-300 ease-soft group-hover:translate-x-1" />
      </Link>
    </section>
  );
}
